import { Outlet, Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/useAuth";
import {
  LayoutDashboard,
  FolderKanban,
  ListTodo,  CalendarDays,
  DollarSign,
  RefreshCw,
  LogOut,
  Bell,
  CheckCheck,
  BarChart3,
  Users,
  Building2,
  Menu,
  X,
  ScrollText,
  UserCog,
  Palette,
  Settings as SettingsIcon,
  Briefcase,
  ShieldCheck,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  Tag,
  Database,
  Activity,
  Key,
  ClipboardList,
  Columns3,
  Wrench,
  Trophy,
  Gauge,
  FileBarChart,
  Package,
  Clock,
} from "lucide-react";
import { listarNotificacoes, contarNaoLidas, marcarComoLida, marcarTodasComoLidas } from "../services/notificacoes";
import { ThemeToggle } from "./ThemeToggle";
import { useEventStream } from "../hooks/useEventStream";
import { useChatNotifications, getSomAtivo, setSomAtivo, getInPageNotifAtivo, setInPageNotifAtivo } from "../hooks/useChatNotifications";
import { useCalendarioLembretes } from "../hooks/useCalendarioLembretes";
import { useTarefasNotificacoes } from "../hooks/useTarefasNotificacoes";
import { useLembretesCalendarioStore } from "../store/lembretesCalendarioStore";
import { usePush } from "../hooks/usePush";
import { caminhoDaNotificacao } from "../utils/notificacaoLink";
import { useNavigate } from "react-router-dom";
import { useBranding } from "../hooks/useBranding";
import { anexoUrlAbsoluto } from "../services/tarefas";
import { Avatar } from "./Avatar";
import { useTituloAba } from "../hooks/useTituloAba";
import { ChatFloating } from "./ChatFloating";
import { ToastNotificacoes } from "./ToastNotificacoes";
import { LembretePlayBloqueante } from "./LembretePlayBloqueante";
import { LembreteCobrancaCliente } from "./LembreteCobrancaCliente";
import { BarraPlayFixa } from "./BarraPlayFixa";

export function Layout() {
  const { user, logout, can } = useAuth();
  const location = useLocation();
  const [showNotifs, setShowNotifs] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Sidebar recolhida (só ícones) — persistido. Só vale em desktop; no mobile o drawer abre por cima.
  const [sidebarColapsada, setSidebarColapsada] = useState<boolean>(() => {
    try { return localStorage.getItem("sidebar_colapsada") === "1"; } catch { return false; }
  });
  function toggleSidebar() {
    setSidebarColapsada((v) => {
      const novo = !v;
      try { localStorage.setItem("sidebar_colapsada", novo ? "1" : "0"); } catch {}
      return novo;
    });
  }
  const qc = useQueryClient();
  useEventStream();
  useChatNotifications();
  useCalendarioLembretes();
  useTarefasNotificacoes();
  const push = usePush();
  const branding = useBranding();
  const navigate = useNavigate();
  useTituloAba(branding?.nome || "RECDC OS");
  const [somAtivo, setSomAtivoState] = useState(getSomAtivo());
  const [inPageAtivo, setInPageAtivoState] = useState(getInPageNotifAtivo());

  const { data: naoLidas } = useQuery({ queryKey: ["notificacoes-count"], queryFn: contarNaoLidas, refetchInterval: 30000 });
  const { data: notificacoes } = useQuery({ queryKey: ["notificacoes"], queryFn: listarNotificacoes, enabled: showNotifs });

  // Lembretes locais (calendário + tarefas)
  const lembretesCalendario = useLembretesCalendarioStore((s) => s.lembretes);
  const marcarLembreteLido = useLembretesCalendarioStore((s) => s.marcarLido);
  const marcarTodosLembretesLidos = useLembretesCalendarioStore((s) => s.marcarTodosLidos);
  const lembretesNaoLidos = lembretesCalendario.filter((l) => !l.lido).length;
  const naoLidasTotal = (naoLidas || 0) + lembretesNaoLidos;

  const marcarLidaM = useMutation({ mutationFn: marcarComoLida, onSuccess: () => { qc.invalidateQueries({ queryKey: ["notificacoes"] }); qc.invalidateQueries({ queryKey: ["notificacoes-count"] }); } });
  const marcarTodasM = useMutation({ mutationFn: marcarTodasComoLidas, onSuccess: () => { qc.invalidateQueries({ queryKey: ["notificacoes"] }); qc.invalidateQueries({ queryKey: ["notificacoes-count"] }); } });

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const navComuns = [
    { path: "/", icon: LayoutDashboard, label: "Dashboard", show: true },
    { path: "/projetos", icon: FolderKanban, label: "Projetos", show: true },
    { path: "/tarefas", icon: ListTodo, label: "Tarefas", show: true },
    { path: "/calendario", icon: CalendarDays, label: "Calendário", show: true },
    { path: "/minha-produtividade", icon: Gauge, label: "Minha produtividade", show: true },
  ];
  const navGerenciar = [
    { path: "/financeiro", icon: DollarSign, label: "Financeiro", show: can(["gestor", "admin"]) },
    { path: "/assinaturas", icon: RefreshCw, label: "Assinaturas", show: can(["gestor", "admin"]) },
    { path: "/relatorios", icon: BarChart3, label: "Relatórios", show: can(["gestor", "admin"]) },
    { path: "/produtividade", icon: Trophy, label: "Produtividade", show: can(["gestor", "admin"]) },
    { path: "/contratado-entregue", icon: FileBarChart, label: "Contratado × entregue", show: can(["gestor", "admin"]) },
    { path: "/entregas-realizadas", icon: Package, label: "Entregas realizadas", show: can(["gestor", "admin"]) },
    { path: "/clientes", icon: Building2, label: "Clientes", show: can(["gestor", "admin"]) },
    { path: "/usuarios", icon: Users, label: "Usuários", show: can(["gestor", "admin"]) },
  ];
  const navAdmin = [
    { path: "/admin/tipos-projeto", icon: Tag, label: "Tipos de projeto", show: can(["gestor", "admin"]) },
    { path: "/admin/tipos-tarefa", icon: ClipboardList, label: "Catálogo (tipos e itens)", show: can(["gestor", "admin"]) },
    { path: "/admin/colunas-kanban", icon: Columns3, label: "Colunas do quadro", show: can(["gestor", "admin"]) },
    { path: "/admin/motivos-ajuste", icon: Wrench, label: "Motivos de ajuste", show: can(["gestor", "admin"]) },
    { path: "/admin/relatorio-ajustes", icon: BarChart3, label: "Relatório de ajustes", show: can(["admin", "gestor"]) },
    { path: "/admin/tags", icon: Tag, label: "Tags", show: can(["gestor", "admin"]) },
    { path: "/admin/tags-projeto", icon: Tag, label: "Tags de projeto", show: can(["gestor", "admin"]) },
    { path: "/admin/setores", icon: Users, label: "Setores da equipe", show: can(["gestor", "admin"]) },
    { path: "/admin/funcoes-tarefa", icon: Briefcase, label: "Funções na tarefa", show: can(["gestor", "admin"]) },
    { path: "/admin/briefings", icon: ClipboardList, label: "Formulários de briefing", show: can(["gestor", "admin"]) },
    { path: "/admin/backups", icon: Database, label: "Backups", show: can(["admin"]) },
    { path: "/admin/agora", icon: Activity, label: "Trabalhando agora", show: can(["admin", "gestor"]) },
    { path: "/aprovacoes-tempo", icon: Clock, label: "Ajustes de tempo", show: can(["admin", "gestor"]) },
    { path: "/admin/api", icon: Key, label: "API", show: can(["admin", "gestor"]) },
    { path: "/auditoria", icon: ScrollText, label: "Auditoria", show: can(["admin"]) },
    { path: "/branding", icon: Palette, label: "Personalização", show: can(["admin"]) },
  ];
  const comunsVis = navComuns.filter((i) => i.show);
  const gerenciarVis = navGerenciar.filter((i) => i.show);
  const adminVis = navAdmin.filter((i) => i.show);
  const visibleItems = [...comunsVis, ...gerenciarVis, ...adminVis];
  const currentLabel = visibleItems.find((i) => isActive(i.path))?.label || "RECDC OS";

  // Estado dos grupos (persistido em localStorage; auto-abre se rota ativa estiver dentro)
  const algumGerenciarAtivo = gerenciarVis.some((i) => isActive(i.path));
  const algumAdminAtivo = adminVis.some((i) => isActive(i.path));
  const [grupoGerenciarAberto, setGrupoGerenciarAberto] = useState(() => {
    const saved = localStorage.getItem("grupo_gerenciar_aberto");
    return saved == null ? true : saved === "1";
  });
  const [grupoAdminAberto, setGrupoAdminAberto] = useState(() => {
    const saved = localStorage.getItem("grupo_admin_aberto");
    return saved == null ? true : saved === "1";
  });
  const gerenciarAtivo = algumGerenciarAtivo || grupoGerenciarAberto;
  const adminAtivo = algumAdminAtivo || grupoAdminAberto;
  function toggleGerenciar() {
    const v = !grupoGerenciarAberto;
    setGrupoGerenciarAberto(v);
    localStorage.setItem("grupo_gerenciar_aberto", v ? "1" : "0");
  }
  function toggleAdmin() {
    const v = !grupoAdminAberto;
    setGrupoAdminAberto(v);
    localStorage.setItem("grupo_admin_aberto", v ? "1" : "0");
  }

  // `colapsada` controla se mostramos só ícones. Usado só na sidebar desktop;
  // o drawer mobile sempre renderiza expandida (drawer já é overlay temporário).
  const renderSidebar = (colapsada: boolean) => (
    <aside className={`${colapsada ? "w-16" : "w-64"} bg-slate-900 text-white flex flex-col shrink-0 h-full transition-[width] duration-200`}>
      <div className={`${colapsada ? "p-2 justify-center" : "p-4 justify-between"} border-b border-slate-800 flex items-center gap-2 min-h-[68px]`}>
        {!colapsada && (branding?.logoUrl ? (
          <div className="flex-1 flex items-center justify-center overflow-hidden">
            <div
              className="flex items-center justify-center max-w-full"
              style={{ height: `${parseInt(branding?.logoEscala || "48")}px` }}
            >
              <img
                src={anexoUrlAbsoluto(branding.logoUrl)}
                alt={branding?.nome || "logo"}
                className="h-full w-auto max-w-full object-contain"
              />
            </div>
          </div>
        ) : (
          <div className="min-w-0">
            <h1 className="text-base font-bold tracking-tight truncate">{branding?.nome || "RECDC OS"}</h1>
            <p className="text-[11px] text-slate-400 mt-0.5">Project Manager</p>
          </div>
        ))}
        {/* Botão colapsar/expandir — só desktop */}
        <button
          onClick={toggleSidebar}
          className="hidden md:flex p-1.5 rounded hover:bg-slate-800 text-slate-400 hover:text-white shrink-0"
          title={colapsada ? "Expandir menu" : "Recolher menu"}
        >
          {colapsada ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
        <button onClick={() => setDrawerOpen(false)} className="md:hidden p-1.5 rounded hover:bg-slate-800 shrink-0">
          <X size={18} />
        </button>
      </div>

      <nav className={`flex-1 ${colapsada ? "p-2" : "p-3"} space-y-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}>
        {comunsVis.map((i) => (
          <Link
            key={i.path}
            to={i.path}
            onClick={() => setDrawerOpen(false)}
            title={colapsada ? i.label : undefined}
            className={`flex items-center ${colapsada ? "justify-center px-2 py-2.5" : "gap-3 px-4 py-2.5"} rounded-lg transition-colors ${
              isActive(i.path) ? "bg-blue-600 text-white shadow-md" : "text-slate-100 hover:bg-slate-700 hover:text-white"
            }`}
          >
            <i.icon size={18} />
            {!colapsada && <span className="text-sm font-medium">{i.label}</span>}
          </Link>
        ))}

        {gerenciarVis.length > 0 && (
          <div className="pt-2">
            {colapsada ? (
              <div className="border-t border-slate-700/60 my-1" />
            ) : (
              <button
                type="button"
                onClick={toggleGerenciar}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  algumGerenciarAtivo ? "text-white" : "text-slate-300 hover:text-white"
                } hover:bg-slate-800/60`}
              >
                <Briefcase size={16} className="text-slate-400" />
                <span className="text-[11px] uppercase font-bold tracking-wider flex-1 text-left">Gerenciar</span>
                <ChevronDown
                  size={14}
                  className={`text-slate-500 transition-transform ${gerenciarAtivo ? "rotate-0" : "-rotate-90"}`}
                />
              </button>
            )}
            {(colapsada || gerenciarAtivo) && (
              <div className={colapsada ? "space-y-1" : "mt-1 ml-3 pl-3 border-l border-slate-700/60 space-y-1"}>
                {gerenciarVis.map((i) => (
                  <Link
                    key={i.path}
                    to={i.path}
                    onClick={() => setDrawerOpen(false)}
                    title={colapsada ? i.label : undefined}
                    className={`flex items-center ${colapsada ? "justify-center px-2 py-2" : "gap-3 px-3 py-2"} rounded-lg transition-colors ${
                      isActive(i.path) ? "bg-blue-600 text-white shadow-md" : "text-slate-100 hover:bg-slate-700 hover:text-white"
                    }`}
                  >
                    <i.icon size={16} />
                    {!colapsada && <span className="text-sm font-medium">{i.label}</span>}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}

        {adminVis.length > 0 && (
          <div className="pt-1">
            {colapsada ? (
              <div className="border-t border-purple-500/40 my-1" />
            ) : (
              <button
                type="button"
                onClick={toggleAdmin}
                className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-colors ${
                  algumAdminAtivo ? "text-white" : "text-slate-300 hover:text-white"
                } hover:bg-slate-800/60`}
              >
                <ShieldCheck size={16} className="text-purple-400" />
                <span className="text-[11px] uppercase font-bold tracking-wider flex-1 text-left">Admin</span>
                <ChevronDown
                  size={14}
                  className={`text-slate-500 transition-transform ${adminAtivo ? "rotate-0" : "-rotate-90"}`}
                />
              </button>
            )}
            {(colapsada || adminAtivo) && (
              <div className={colapsada ? "space-y-1" : "mt-1 ml-3 pl-3 border-l border-purple-500/40 space-y-1"}>
                {adminVis.map((i) => (
                  <Link
                    key={i.path}
                    to={i.path}
                    onClick={() => setDrawerOpen(false)}
                    title={colapsada ? i.label : undefined}
                    className={`flex items-center ${colapsada ? "justify-center px-2 py-2" : "gap-3 px-3 py-2"} rounded-lg transition-colors ${
                      isActive(i.path) ? "bg-blue-600 text-white shadow-md" : "text-slate-100 hover:bg-slate-700 hover:text-white"
                    }`}
                  >
                    <i.icon size={16} />
                    {!colapsada && <span className="text-sm font-medium">{i.label}</span>}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </nav>

      <div className={`${colapsada ? "p-2" : "p-3"} border-t border-slate-800`}>
        {colapsada ? (
          <div className="flex flex-col items-center gap-1">
            <Link
              to="/perfil"
              onClick={() => setDrawerOpen(false)}
              className="p-1.5 rounded-lg hover:bg-slate-800 transition-colors"
              title={user?.nome ? `${user.nome} — Meu perfil` : "Meu perfil"}
            >
              <Avatar nome={user?.nome} fotoUrl={(user as any)?.fotoUrl} tamanho={32} bgFallback="bg-blue-500" textFallback="text-white" />
            </Link>
            <button
              onClick={logout}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-red-600/20 hover:!text-red-300 transition-colors"
              title="Sair"
              aria-label="Sair"
            >
              <LogOut size={18} />
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <Link
              to="/perfil"
              onClick={() => setDrawerOpen(false)}
              className="flex-1 min-w-0 flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-800 transition-colors"
            >
              <Avatar nome={user?.nome} fotoUrl={(user as any)?.fotoUrl} tamanho={36} bgFallback="bg-blue-500" textFallback="text-white" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.nome}</p>
                <p className="text-xs text-slate-400 capitalize flex items-center gap-1">
                  <UserCog size={11} /> Meu perfil
                </p>
              </div>
            </Link>
            <button
              onClick={logout}
              className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-red-600/20 hover:!text-red-300 transition-colors shrink-0"
              title="Sair"
              aria-label="Sair"
            >
              <LogOut size={18} />
            </button>
          </div>
        )}
      </div>
    </aside>
  );

  return (
    <div className="flex h-screen bg-slate-50 print:block print:h-auto print:bg-white">
      {/* Sidebar fixa em md+ — usa estado de colapsada. Some na impressão (PDF do relatório). */}
      <div className="hidden md:flex print:!hidden">{renderSidebar(sidebarColapsada)}</div>

      {/* Drawer mobile — sempre expandida (overlay temporário) */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawerOpen(false)} />
          <div className="relative z-10">{renderSidebar(false)}</div>
        </div>
      )}

      <main className="flex-1 flex flex-col min-w-0 print:block">
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3 shrink-0 print:hidden">
          <div className="flex items-center gap-2 min-w-0">
            <button
              onClick={() => setDrawerOpen(true)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 shrink-0"
              aria-label="Abrir menu"
            >
              <Menu size={20} className="text-slate-600" />
            </button>
            <h2 className="text-base sm:text-lg font-semibold text-slate-800 truncate">{currentLabel}</h2>
          </div>

          <div className="flex items-center gap-1">
            <ThemeToggle />
          <div className="relative">
            <button
              onClick={() => setShowNotifs(!showNotifs)}
              className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
              aria-label="Notificações"
            >
              <Bell size={20} className="text-slate-600" />
              {naoLidasTotal > 0 && (
                <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {naoLidasTotal > 99 ? "99+" : naoLidasTotal}
                </span>
              )}
            </button>

            {showNotifs && (
              <div className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl border border-slate-200 shadow-2xl z-50 overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                  <h3 className="font-semibold text-sm">{showConfig ? "Configurações" : "Notificações"}</h3>
                  <div className="flex items-center gap-1">
                    {!showConfig && naoLidasTotal > 0 && (
                      <button
                        onClick={() => {
                          if ((naoLidas || 0) > 0) marcarTodasM.mutate();
                          if (lembretesNaoLidos > 0) marcarTodosLembretesLidos();
                        }}
                        className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <CheckCheck size={12} /> Marcar todas
                      </button>
                    )}
                    <button
                      onClick={() => setShowConfig(!showConfig)}
                      className={`p-1 rounded ${showConfig ? "text-blue-600 bg-blue-50" : "text-slate-400 hover:text-slate-600 hover:bg-slate-100"}`}
                      title="Configurações"
                    >
                      <SettingsIcon size={14} />
                    </button>
                  </div>
                </div>

                {showConfig ? (
                  <div className="px-4 py-3 space-y-3 text-xs text-slate-700">
                    <ConfigItem
                      label="🔔 Notificações no navegador"
                      sub={statusPushDescricao(push.status)}
                      botao={
                        <button
                          onClick={async () => {
                            if (push.status === "ativo") await push.desativar();
                            else await push.ativar();
                          }}
                          className={`text-[10px] px-2 py-0.5 rounded ${push.status === "ativo" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}
                        >
                          {push.status === "ativo" ? "Desativar" : push.status === "negado" ? "Bloqueado" : push.status === "indisponivel" ? "Indisp." : "Ativar"}
                        </button>
                      }
                    />
                    <ConfigItem
                      label="📩 Toast quando aba em background"
                      sub="Notificação dentro do browser quando você está em outra aba"
                      botao={
                        <input
                          type="checkbox"
                          checked={inPageAtivo}
                          onChange={async (e) => {
                            const v = e.target.checked;
                            if (v && "Notification" in window && Notification.permission !== "granted") {
                              await Notification.requestPermission();
                            }
                            setInPageNotifAtivo(v); setInPageAtivoState(v);
                          }}
                        />
                      }
                    />
                    <ConfigItem
                      label="🔉 Som ao receber mensagem"
                      sub="Toca um 'ding' discreto"
                      botao={
                        <input
                          type="checkbox"
                          checked={somAtivo}
                          onChange={(e) => { setSomAtivo(e.target.checked); setSomAtivoState(e.target.checked); }}
                        />
                      }
                    />

                    {/* Diagnóstico push */}
                    <div className="pt-2 border-t border-slate-100">
                      <p className="text-[10px] uppercase font-bold text-slate-500 mb-1">Testar push</p>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={async () => {
                            try {
                              const { disparoLocalDeTeste } = await import("../services/push");
                              await disparoLocalDeTeste();
                            } catch (e: any) {
                              alert("Falha local: " + e.message);
                            }
                          }}
                          className="text-[11px] flex-1 bg-slate-100 hover:bg-slate-200 px-2 py-1.5 rounded"
                        >
                          Local (instantâneo)
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const { testPush } = await import("../services/push");
                              await testPush();
                              alert("Push enviado pelo servidor — pode levar alguns segundos.");
                            } catch (e: any) {
                              alert("Falha no servidor: " + e.message);
                            }
                          }}
                          className="text-[11px] flex-1 bg-blue-600 hover:bg-blue-700 text-white px-2 py-1.5 rounded"
                        >
                          Via servidor
                        </button>
                      </div>
                      {push.status === "negado" && (
                        <p className="text-[10px] text-red-600 mt-1">
                          Você bloqueou notificações desse site. Vá nas configs do browser → permitir.
                        </p>
                      )}
                      {push.status === "indisponivel" && (
                        <p className="text-[10px] text-amber-600 mt-1">
                          Push exige HTTPS ou localhost. Acesse via https:// ou pelo tunnel.
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="max-h-72 overflow-y-auto">
                    {/* Lembretes locais primeiro (calendário e tarefas) */}
                    {[...lembretesCalendario]
                      .sort((a, b) => b.criadoEm.localeCompare(a.criadoEm))
                      .map((l) => (
                      <div
                        key={`lem-${l.id}`}
                        onClick={() => {
                          if (!l.lido) marcarLembreteLido(l.id);
                          if (l.link) navigate(l.link);
                          setShowNotifs(false);
                        }}
                        className={`px-4 py-3 border-b border-slate-50 cursor-pointer hover:bg-slate-50 transition-colors ${
                          !l.lido ? "bg-blue-50/50" : ""
                        }`}
                      >
                        <p className="text-sm font-medium text-slate-800 flex items-center gap-1.5">
                          <span>{l.emoji}</span>
                          <span className="flex-1">{l.titulo}</span>
                          <span className="text-[9px] uppercase font-bold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded shrink-0">Lembrete</span>
                        </p>
                        <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{l.mensagem}</p>
                        <p className="text-[10px] text-slate-400 mt-1 flex items-center gap-1.5" title={new Date(l.criadoEm).toLocaleString("pt-BR")}>
                          <span>{formatarDataHora(l.criadoEm)}</span>
                          <span>·</span>
                          <span className="font-medium">{tempoRelativo(l.criadoEm)}</span>
                        </p>
                      </div>
                    ))}

                    {notificacoes?.length === 0 && lembretesCalendario.length === 0 && (
                      <p className="px-4 py-6 text-center text-sm text-slate-400">Sem notificações</p>
                    )}
                    {notificacoes?.map((n) => {
                      // Destaques visuais por tipo:
                      //  - @menção: âmbar (urgente/pessoal)
                      //  - foco_*: verde (lembrete de "Estou nessa")
                      //  - resto: azul claro padrão pra não lidas
                      const ehMencao = n.tipo === "tarefa_mencao";
                      const ehFoco = n.tipo?.startsWith("foco_");
                      const bgClasses = ehMencao
                        ? (n.lida ? "bg-amber-50/40 border-l-4 border-l-amber-300" : "bg-amber-100 border-l-4 border-l-amber-500")
                        : ehFoco
                          ? (n.lida ? "bg-emerald-50/40 border-l-4 border-l-emerald-300" : "bg-emerald-50 border-l-4 border-l-emerald-500")
                          : (!n.lida ? "bg-blue-50/50" : "");
                      const chipLabel = ehMencao ? "@menção" : ehFoco ? "foco" : null;
                      const chipClasses = ehMencao
                        ? "bg-amber-500 text-white"
                        : ehFoco
                          ? "bg-emerald-600 text-white"
                          : "";
                      return (
                      <div
                        key={n.id}
                        onClick={() => {
                          if (!n.lida) marcarLidaM.mutate(n.id);
                          const destino = caminhoDaNotificacao(n, user?.perfil);
                          if (destino) navigate(destino);
                          setShowNotifs(false);
                        }}
                        className={`px-4 py-3 border-b border-slate-50 cursor-pointer hover:bg-slate-50 transition-colors ${bgClasses}`}
                      >
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {chipLabel && (
                            <span className={`text-[9px] uppercase font-bold tracking-wide px-1.5 py-0.5 rounded ${chipClasses}`}>
                              {chipLabel}
                            </span>
                          )}
                          <p className={`text-sm font-medium ${ehMencao ? "text-amber-900" : ehFoco ? "text-emerald-900" : "text-slate-800"}`}>{n.titulo}</p>
                        </div>
                        <p className={`text-xs mt-0.5 line-clamp-2 ${ehMencao ? "text-amber-800/80" : ehFoco ? "text-emerald-800/80" : "text-slate-500"}`}>{n.mensagem}</p>
                        <p className={`text-[10px] mt-1 flex items-center gap-1.5 ${ehMencao ? "text-amber-700/60" : ehFoco ? "text-emerald-700/60" : "text-slate-400"}`} title={new Date(n.criadoEm).toLocaleString("pt-BR")}>
                          <span>{formatarDataHora(n.criadoEm)}</span>
                          <span>·</span>
                          <span className="font-medium">{tempoRelativo(n.criadoEm)}</span>
                        </p>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
          </div>
        </header>

        {/* Kanban de Tarefas usa a LARGURA TOTAL (mais colunas visíveis, cara de Trello);
            as demais telas mantêm o limite max-w-7xl centralizado.
            Em md+ a aba de Tarefas também não rola a PÁGINA (pedido da Rec, jul/2026):
            ela cabe na tela e quem rola é cada coluna. No mobile mantém a rolagem
            normal — sem ela, header + filtros empilhados não deixariam altura pro quadro.
            Segue overflow-auto (não hidden) de propósito: com o quadro em flex-1 nada
            estoura e a barra não aparece; se um dia estourar, o usuário rola em vez de
            perder o conteúdo cortado. */}
        <div
          className={`flex-1 overflow-auto p-4 sm:p-6 lg:p-8 w-full print:overflow-visible print:p-0 print:max-w-none ${
            location.pathname.startsWith("/tarefas")
              ? "md:flex md:flex-col md:min-h-0"
              : "max-w-7xl mx-auto"
          }`}
        >
          <Outlet />
        </div>
      </main>

      <ChatFloating />
      <ToastNotificacoes />
      <LembretePlayBloqueante />
      <LembreteCobrancaCliente />
      <BarraPlayFixa />
    </div>
  );
}

function ConfigItem({ label, sub, botao }: { label: string; sub?: string; botao: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2">
      <div className="flex-1 min-w-0">
        <p className="text-slate-700">{label}</p>
        {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
      </div>
      <div className="shrink-0 mt-0.5">{botao}</div>
    </div>
  );
}

function statusPushDescricao(s: string) {
  if (s === "ativo") return "Você receberá pushes mesmo com o site fechado.";
  if (s === "negado") return "Permissão bloqueada — desbloqueie nas configs do browser.";
  if (s === "indisponivel") return "Browser não suporta ou está em http (não-localhost).";
  if (s === "carregando") return "Verificando…";
  return "Clique em 'Ativar' para receber pushes do navegador.";
}

// Formata a data:
// - hoje  → "14:32"
// - este ano → "28/04 14:32"
// - outros → "28/04/2025 14:32"
function formatarDataHora(iso: string): string {
  const d = new Date(iso);
  const agora = new Date();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const mon = String(d.getMonth() + 1).padStart(2, "0");
  const mesmoDia = d.toDateString() === agora.toDateString();
  if (mesmoDia) return `${hh}:${mm}`;
  if (d.getFullYear() === agora.getFullYear()) return `${dd}/${mon} ${hh}:${mm}`;
  return `${dd}/${mon}/${d.getFullYear()} ${hh}:${mm}`;
}

// "há agora", "há 5 min", "há 2h", "há 3d", "há 2 sem"
function tempoRelativo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "agora";
  const seg = Math.floor(ms / 1000);
  if (seg < 45) return "há agora";
  const min = Math.floor(seg / 60);
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `há ${d}d`;
  const sem = Math.floor(d / 7);
  if (sem < 5) return `há ${sem} sem`;
  const meses = Math.floor(d / 30);
  if (meses < 12) return `há ${meses} mês${meses !== 1 ? "es" : ""}`;
  const anos = Math.floor(d / 365);
  return `há ${anos}a`;
}
